<script setup lang="ts">
import type { DisciplineStageLogRow } from '../model/discipline-stage-logs.hook'

import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { faCrown } from '@fortawesome/pro-solid-svg-icons'

import { AppBadge, BadgeCustomerBrand } from '@shared/ui/badges'
import { AppText, AppTextLink } from '@shared/ui/typography'
import { AppTooltip } from '@shared/ui/tooltips'
import { BadgeKanbanStageStatus, KanbanPriority } from '@shared/ui/kanban'
import { AppIcon } from '@shared/ui/icons'

defineOptions({
  name: 'DisciplineStageLogItem',
})

defineProps<{
  row: DisciplineStageLogRow
  kanbanPriority: Record<string, number>
}>()
</script>

<template>
  <div :class="['kanban-log-item', { main: row.isGroupStart, last: row.isGroupEnd }]">
    <div
      v-if="row.isGroupStart && row.stage"
      class="kanban-log-item__stage"
    >
      <BadgeKanbanStageStatus
        :text="row.stage.text"
        :color="row.stage.color"
      />
    </div>
    <div class="kanban-log-item__general">
      <AppText
        name="body2"
        color="slate-500"
        class="kanban-log-item__date"
      >
        {{ row.date }}
      </AppText>
      <div class="kanban-log-item__info discipline-stage-log">
        <div class="discipline-stage-log__actor">
          <AppText
            v-if="row.view.isAutomatic"
            name="body2"
            color="slate-400"
            class="discipline-stage-log__muted"
          >
            changed automatically
          </AppText>
          <div
            v-else-if="row.customerBadge"
            class="discipline-stage-log__customer"
          >
            <AppTooltip
              v-if="row.customerBadge.isPremium"
              text="Premium tutor bought"
              size="small"
            >
              <AppIcon
                :icon="faCrown"
                size="small"
                color="amber-500"
              />
            </AppTooltip>
            <AppBadge
              v-if="row.customerBadge.isGo"
              color="emerald"
              text="Goal"
              title="GO lead"
            />
            <BadgeCustomerBrand
              :brand="row.customerBadge.brand"
              view="icon"
            />
            <AppTextLink
              v-if="row.customerBadge.url"
              name="body2-medium"
              color="blue-700"
              :href="row.customerBadge.url"
              :title="row.customerBadge.firstName"
              target="_blank"
            >
              {{ row.customerBadge.firstName }}
            </AppTextLink>
          </div>
          <template v-else>
            <AppTextLink
              v-if="row.actorUrl && row.actorName"
              name="body2-medium"
              color="blue-700"
              :href="row.actorUrl"
              target="_blank"
            >
              {{ row.actorName }}
            </AppTextLink>
            <AppText
              v-else-if="row.actorName"
              name="body2-medium"
              color="blue-700"
            >
              {{ row.actorName }}
            </AppText>
            <AppText
              name="body2"
              color="slate-500"
            >
              {{ row.view.label }}
            </AppText>
          </template>
          <template v-if="row.view.comment">
            <AppBadge
              v-for="channel in row.view.comment.channels"
              :key="channel"
              color="blue"
              :text="channel"
            />
            <AppBadge
              v-if="row.view.comment.reach"
              color="slate"
              :text="row.view.comment.reach"
            />
          </template>
        </div>

        <div
          v-if="row.view.stages"
          class="discipline-stage-log__row"
        >
          <BadgeKanbanStageStatus
            v-if="row.stageFrom"
            :text="row.stageFrom.text"
            :color="row.stageFrom.color"
          />
          <AppIcon
            v-if="row.stageFrom"
            :icon="faArrowRight"
            size="small"
          />
          <BadgeKanbanStageStatus
            v-if="row.stageTo"
            :text="row.stageTo.text"
            :color="row.stageTo.color"
          />
        </div>

        <div
          v-else-if="row.view.priorities"
          class="discipline-stage-log__row"
        >
          <KanbanPriority
            v-if="row.view.priorities.from"
            :status="row.view.priorities.from"
            :kanban-priority="kanbanPriority"
            size="medium"
          />
          <AppIcon
            v-if="row.view.priorities.from"
            :icon="faArrowRight"
            size="small"
          />
          <KanbanPriority
            v-if="row.view.priorities.to"
            :status="row.view.priorities.to"
            :kanban-priority="kanbanPriority"
            size="medium"
          />
        </div>

        <div
          v-else-if="row.view.change"
          class="discipline-stage-log__row"
        >
          <template v-if="row.view.change.from">
            <AppText
              name="body2"
              color="slate-900"
              class="discipline-stage-log__value"
            >
              {{ row.view.change.from }}
            </AppText>
            <AppIcon
              :icon="faArrowRight"
              size="small"
            />
          </template>
          <AppText
            name="body2"
            color="slate-900"
            class="discipline-stage-log__value"
          >
            {{ row.view.change.to }}
          </AppText>
        </div>

        <div
          v-else-if="row.view.sections"
          class="discipline-stage-log__sections"
        >
          <div
            v-for="section in row.view.sections"
            :key="section.label"
            class="discipline-stage-log__section"
          >
            <AppText
              name="body2-medium"
              color="slate-500"
            >
              {{ section.label }}
            </AppText>
            <div class="discipline-stage-log__row">
              <AppText
                name="body2"
                color="slate-900"
                class="discipline-stage-log__value"
              >
                {{ section.from }}
              </AppText>
              <AppIcon
                :icon="faArrowRight"
                size="small"
              />
              <AppText
                name="body2"
                color="slate-900"
                class="discipline-stage-log__value"
              >
                {{ section.to }}
              </AppText>
            </div>
          </div>
        </div>

        <AppText
          v-else-if="row.view.comment?.text"
          name="body2"
          color="slate-900"
          class="discipline-stage-log__value"
        >
          {{ row.view.comment.text }}
        </AppText>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss" src="@shared/ui/kanban/styles/_kanban-log-item.scss" />
<style scoped lang="scss" src="./DisciplineStageLogItem.scss" />
